// school-ai/src/i18n/config.ts
import { useSyncExternalStore } from "react";
import { ja } from "./locales/ja";
import { en } from "./locales/en";
import { safeLocalStorage } from "@/utils/safeBrowserStorage";

export const supportedLngs = {
  en: "English",
  ja: "日本語"
} as const;

export type Lang = keyof typeof supportedLngs;

type Dict = { [key: string]: string | Dict };

export const resources: Record<Lang, Dict> = { ja, en };

const STORAGE_KEY = "i18nextLng";

const normalize = (value: string | null | undefined): Lang | null => {
  if (!value) return null;
  if (value.startsWith("ja")) return "ja";
  if (value.startsWith("en")) return "en";
  return null;
};

// このモジュールは main.tsx から副作用 import され、モジュール評価時に実行される。
// Safari で Cookie/ストレージが遮断された環境（設定・iframe 埋め込み等）では
// localStorage へのアクセス自体が SecurityError を投げ、直に触るとアプリ全体が
// 起動前に落ちる（Sentry SCHOOL-AI-5Q）。必ず safeLocalStorage 経由で読む。
const detectInitialLang = (): Lang =>
  normalize(safeLocalStorage.getItem(STORAGE_KEY)) ??
  normalize(typeof navigator === "undefined" ? null : navigator.language) ??
  "ja";

let currentLang: Lang = detectInitialLang();
const listeners = new Set<() => void>();

const syncDocumentLang = (lang: Lang): void => {
  if (typeof document === "undefined") return;
  document.documentElement.lang = lang;
};
syncDocumentLang(currentLang);

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = (): Lang => currentLang;

/** 現在の表示言語。t() はこの値で文言を引く。dayjs の locale 名としてもそのまま使える */
export const getLanguage = (): Lang => currentLang;

/** Intl / toLocaleString 用のロケール（日付・数値の書式を表示言語に合わせる） */
export const getIntlLocale = (): string =>
  currentLang === "ja" ? "ja-JP" : "en-US";

/**
 * 表示言語を切り替える（ページの読み込み直しはしない）。
 * 画面全体を切り替えるときは changeLanguage を使う。テストなど、言語だけを
 * 差し替えたいときに使う。
 */
export const setLanguage = (lang: Lang): void => {
  if (lang === currentLang) return;
  currentLang = lang;
  safeLocalStorage.setItem(STORAGE_KEY, lang);
  syncDocumentLang(lang);
  listeners.forEach(listener => listener());
};

/**
 * 表示言語を切り替えて、ページを読み込み直す。
 *
 * 文言は t() で原文（日本語）から引くが、選択肢やルート名のようにモジュールの
 * 読み込み時に確定する定数や、useMemo に乗った値は再描画だけでは切り替わらない。
 * ページ全体を読み直して、全画面を同じ言語に揃える。
 */
const changeLanguage = (lang: Lang): void => {
  if (lang === currentLang) return;
  setLanguage(lang);
  if (typeof window !== "undefined") window.location.reload();
};

export const useLanguage = (): {
  language: Lang;
  changeLanguage: (lang: Lang) => void;
} => {
  const language = useSyncExternalStore(subscribe, getSnapshot);
  return { language, changeLanguage };
};
